import React, { useEffect, useCallback } from "react";
import { Card, Row, Col, Button, DatePicker } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { setFilters } from "../AccountBalanceSlice";
import {
  fetchAccountBalance,
  fetchBankAccountsBySearch,
  fetchCashAccountsBySearch,
} from "../AccountBalanceThunk";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useLocation, useSearchParams } from "react-router-dom";
import { toCompanyDateOnlyforReportDateFilter } from "../../../lib/functions";

const { RangePicker } = DatePicker;

const AccountBalanceFilters = () => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.accountBalance);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const path = pathname.split("/")[3];
    if (path) {
      // Balance Sheet passes full ISO instants; this report's thunk expects
      // date-only (YYYY-MM-DD) strings, so normalize to the company timezone.
      const rawFrom = searchParams.get("fromDate");
      const toDate = toCompanyDateOnlyforReportDateFilter(path).format(
        "YYYY-MM-DD"
      );
      const dateFilters = rawFrom
        ? {
            fromDate: toCompanyDateOnlyforReportDateFilter(rawFrom).format(
              "YYYY-MM-DD"
            ),
            toDate,
          }
        : { toDate };
      dispatch(setFilters(dateFilters));
      dispatch(fetchAccountBalance({ filters: { ...filters, ...dateFilters } }));
    }
  }, []);

  const searchBankAccount = useCallback(
    (text) => dispatch(fetchBankAccountsBySearch(text)),
    [dispatch]
  );
  const searchCashAccount = useCallback(
    (text) => dispatch(fetchCashAccountsBySearch(text)),
    [dispatch]
  );

  const handleFilterChange = (field, value) => {
    if (field === "filterType") {
      dispatch(setFilters({ accId: null })); // reset accountId
    }
    dispatch(setFilters({ [field]: value }));
  };

  const handleSubmit = () => {
    dispatch(fetchAccountBalance({ filters }));
  };

  const handleDateRangeChange = (dates, dateStrings) => {
    if (!dates) {
      dispatch(setFilters({ fromDate: null, toDate: null }));
      return;
    }
    dispatch(
      setFilters({ fromDate: dateStrings[0], toDate: dateStrings[1] })
    );
  };


  const renderExtraField = () => {
    switch (filters.filterType) {
      case "Cash Wise":
        return (
          <Col xs={24} sm={12} md={12} lg={6}>
            <label>Search Account</label>
            <SearchListAutoComplete
              entityType="account"
              searchThunk={searchCashAccount}
              resultSelector={(state) => state.accountBalance.cashAccounts}
              onSelect={(obj) => handleFilterChange("accId", obj?.sale_id)}
              placeholder="Account"
              showAddNew={false}
            />
          </Col>
        );

      case "Bank account Wise":
        return (
          <Col xs={24} sm={12} md={12} lg={6}>
            <label>Search Account</label>
            <SearchListAutoComplete
              entityType="account"
              searchThunk={searchBankAccount}
              resultSelector={(state) => state.accountBalance.bankAccounts}
              onSelect={(obj) => handleFilterChange("accId", obj?.sale_id)}
              placeholder="Account"
              showAddNew={false}
            />
          </Col>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={16} style={{ alignItems: "center" }}>
        <Col xs={24} sm={12} md={12} lg={6}>
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "Cash & Bank", display_text: "Cash & Bank" },
              { sale_id: "Cash Wise", display_text: "Cash Wise" },
              {
                sale_id: "Bank account Wise",
                display_text: "Bank Account Wise",
              },
            ]}
            value={{ value: filters.filterType, label: filters.filterType }}
            onSelect={(obj) => handleFilterChange("filterType", obj?.sale_id)}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {renderExtraField()}

        <Col xs={24} sm={12} md={12} lg={6}>
          <label>Date Range</label>
          <RangePicker
            style={{ width: "100%" }}
            value={
              filters.fromDate && filters.toDate
                ? [dayjs(filters.fromDate), dayjs(filters.toDate)]
                : null
            }
            format="YYYY-MM-DD"
            onChange={handleDateRangeChange}
          />
        </Col>

        <Col
          xs={24}
          sm={12}
          md={12}
          lg={4}
          style={{ display: "flex", alignItems: "flex-end", height: 58 }}
        >
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSubmit}
            block
            data-testid="report-btn"
          >
            Get Report
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default AccountBalanceFilters;
